import React, {PropsWithChildren, useEffect} from 'react';
import {BrowserRouter, Route, Routes} from "react-router-dom";
import {Alert, Button, ThemeOptions} from "@mui/material";
import {ErrorBoundary, usePaletteMode, VCThemeProvider} from "@variocube/app-ui";
import {StorageLocalizationProvider, useLocalization} from "./i18n";
import {TenantContextProvider, useTenant} from "./context/tenant";
import {SiteList} from "./page/site-list";
import {SiteRoutes} from "./page/site-routes";
import {BookingRoutes} from "./page/booking-routes";

export function App() {
	return (
		<ErrorBoundary>
			<BrowserRouter>
				<TenantContextProvider>
					<AppThemeProvider>
						<AppShell />
					</AppThemeProvider>
				</TenantContextProvider>
			</BrowserRouter>
		</ErrorBoundary>
	);
}

function AppThemeProvider({children}: PropsWithChildren<unknown>) {
	const {tenant: { branding: { muiThemeOptions, ...branding } = {} } = {}} = useTenant();
	const {palette, ...options}: ThemeOptions = muiThemeOptions ?? {};

	return(
		<VCThemeProvider
			branding={{
				...branding,
				muiThemeOptions: {
					palette: {
						...(palette ?? {}),
						mode: "light",
					},
					...options,
				}
			}}
		>
			{children}
		</VCThemeProvider>
	);
}

function AppShell() {
	const {mode, setMode} = usePaletteMode();

	useEffect(() => {
		if (mode !== 'light') {
			setMode('light');
			console.info('Forcing light mode until dark mode is supported.');
		}
	}, [setMode])

	return (
		<StorageLocalizationProvider>
			<Routes>
				<Route path="/:tenantId" element={<SiteList />} />
				<Route path="/:tenantId/bookings/:bookingId" element={<BookingRoutes />}>
					<Route path="/:tenantId/bookings/:bookingId/:view" element={<BookingRoutes />} />
				</Route>
				<Route path={"/:tenantId/:siteId"} element={<SiteRoutes />}>
					<Route path="/:tenantId/:siteId/:step" element={<SiteRoutes />} />
				</Route>
				<Route path="*" element={<NoMatchRoute />} />
			</Routes>
		</StorageLocalizationProvider>
	)
}

function NoMatchRoute() {
	const {t} = useLocalization();
	return (
		<Alert
			severity="error"
			action={
				<Button variant="contained" component="a" href="/">{t('common.toHomeCta')}</Button>
			}
		>
			{t('common.notFound')}
		</Alert>
	);
}
